"use client";

import {JSX, useEffect, useMemo, useRef, useState} from "react";

import Image from "next/image";

import {assetPath} from "@/shared/utils/asset-path";
import {getStaticPathIcon} from "@/shared/utils/get-static-path-icon";
import {getEmbedUrl, isEmbedVideo} from "@/shared/utils/video";

import "./styles/video-loader.scss";

export interface VideoLoaderProps {
    src: string;
    poster?: string;
    className?: string;
    muted?: boolean;
    autoPlay?: boolean;
    controls?: boolean;
    loop?: boolean;
    playsInline?: boolean;
    showPlayButton?: boolean;
    onPlay?: () => void;
    playing?: boolean;
}

export function VideoLoader({
    src,
    poster,
    className = "",
    muted = false,
    autoPlay = false,
    controls = true,
    loop = false,
    playsInline = true,
    showPlayButton = false,
    onPlay,
    playing,
}: VideoLoaderProps): JSX.Element {
    // Determine if this is an embed (YouTube/Vimeo) or direct video file
    const isEmbed = isEmbedVideo(src);

    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isPlaying, setIsPlaying] = useState(autoPlay);
    const videoRef = useRef<HTMLVideoElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Generate embed URL dynamically
    // Based on react-lite-youtube-embed approach: https://github.com/ibrahimcesar/react-lite-youtube-embed
    const embedUrl = useMemo(() => {
        if (!isEmbed) return src;

        const baseEmbedUrl = getEmbedUrl(src);
        if (!baseEmbedUrl) return src;

        const url = new URL(baseEmbedUrl);
        const isYouTube = src.includes("youtube.com") || src.includes("youtu.be");

        // Autoplay logic (from react-lite-youtube-embed):
        // - When user clicks play button → ALWAYS autoplay (immediate interaction)
        // - When loading without play button → only autoplay if muted (iOS requirement)
        const shouldAddAutoplay = showPlayButton
            ? true // User interaction - always works on iOS
            : autoPlay && muted; // No interaction - requires muted

        if (shouldAddAutoplay) {
            url.searchParams.set("autoplay", "1");
        }

        // CRITICAL: YouTube parameter is "mute" NOT "muted" (HTML5 video uses "muted")
        // Always mute on autoplay for maximum iOS compatibility
        if (isYouTube && (muted || shouldAddAutoplay)) {
            url.searchParams.set("mute", "1");
        } else if (!isYouTube && muted) {
            // Vimeo uses standard "muted" parameter
            url.searchParams.set("muted", "1");
        }

        return url.toString();
    }, [isEmbed, src, autoPlay, muted, showPlayButton]);

    // Stop video when controlled `playing` prop switches to false
    useEffect(() => {
        if (playing !== false) return;

        if (!isEmbed && videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
        setIsPlaying(false);
    }, [playing, isEmbed]);

    // Handle play button click
    // Following react-lite-youtube-embed: just toggle state, let React handle re-render
    const handlePlay = (): void => {
        if (!isEmbed && videoRef.current) {
            const video = videoRef.current;
            if (video.readyState < 3) {
                setIsLoading(true);
            }
            void video.play();
            video.setAttribute("controls", "true");
            setIsPlaying(true);
        } else if (isEmbed) {
            setIsLoading(true);
            setIsPlaying(true);
        }
        onPlay?.();
    };

    // Handle video element load
    useEffect(() => {
        if (!isEmbed && videoRef.current) {
            const video = videoRef.current;

            const handleCanPlay = (): void => {
                setIsLoading(false);
            };

            const handleError = (): void => {
                setIsLoading(false);
                setHasError(true);
            };

            const handlePlay = (): void => {
                setIsPlaying(true);
            };

            const handlePause = (): void => {
                setIsPlaying(false);
            };

            video.addEventListener("canplay", handleCanPlay);
            video.addEventListener("error", handleError);
            video.addEventListener("play", handlePlay);
            video.addEventListener("pause", handlePause);

            // If video is already ready
            if (video.readyState >= 3) {
                setIsLoading(false);
            }

            return () => {
                video.removeEventListener("canplay", handleCanPlay);
                video.removeEventListener("error", handleError);
                video.removeEventListener("play", handlePlay);
                video.removeEventListener("pause", handlePause);
            };
        }
    }, [isEmbed, src]);

    // Handle iframe load (for YouTube/Vimeo)
    useEffect(() => {
        if (isEmbed && isPlaying && iframeRef.current) {
            const iframe = iframeRef.current;

            const handleIframeLoad = (): void => {
                setIsLoading(false);
            };

            const handleIframeError = (): void => {
                setIsLoading(false);
                setHasError(true);
            };

            iframe.addEventListener("load", handleIframeLoad);
            iframe.addEventListener("error", handleIframeError);

            return () => {
                iframe.removeEventListener("load", handleIframeLoad);
                iframe.removeEventListener("error", handleIframeError);
            };
        }
    }, [isEmbed, isPlaying]);

    return (
        <div className={`video-loader ${className} ${isPlaying ? "video-loader--playing" : "video-loader--paused"}`}>
            {/* Loading Spinner */}
            {isLoading && !hasError && (
                <div className="video-loader__spinner">
                    <div className="video-loader__spinner-icon" />
                </div>
            )}

            {/* Error State */}
            {hasError && (
                <div className="video-loader__error">
                    <Image src={getStaticPathIcon("warning")} alt="" width={48} height={48} />
                    <p>Failed to load video</p>
                </div>
            )}

            {/* Video Content */}
            {isEmbed ? (
                <>
                    {/* Poster with play button for embed videos */}
                    {!isPlaying && poster && showPlayButton && (
                        <div className="video-loader__poster">
                            <Image src={poster} fill alt="Video poster" priority />
                            <button className="video-loader__play-btn" onClick={handlePlay} type="button" aria-label="Play video">
                                <Image src={assetPath("/images/icons/play-btn.svg")} width={50} height={50} alt="Play button" />
                            </button>
                        </div>
                    )}
                    {/* Iframe - show when playing, no poster/play button, or no poster available */}
                    {(isPlaying || !showPlayButton || !poster) && (
                        <iframe
                            ref={iframeRef}
                            className="video-loader__iframe"
                            src={embedUrl}
                            title="Video player"
                            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                            style={{opacity: isLoading ? 0 : 1}}
                        />
                    )}
                </>
            ) : (
                <>
                    {/* HTML5 Video element */}
                    <video
                        ref={videoRef}
                        className="video-loader__video"
                        src={src}
                        poster={poster}
                        muted={muted}
                        autoPlay={autoPlay}
                        controls={controls}
                        loop={loop}
                        playsInline={playsInline}
                        preload={autoPlay ? "auto" : "none"}
                        style={{opacity: isLoading ? 0 : 1}}
                    >
                        <track kind="captions" />
                        Your browser does not support the video tag.
                    </video>

                    {/* Play button for video files - only show if poster exists */}
                    {!isPlaying && showPlayButton && poster && (
                        <button className="video-loader__play-btn" onClick={handlePlay} type="button" aria-label="Play video">
                            <Image src={assetPath("/images/icons/play-btn.svg")} width={50} height={50} alt="Play button" />
                        </button>
                    )}
                </>
            )}
        </div>
    );
}
